/* ──────────────────────────────────────────────────────────────────────────
 * Dashboard - New — standalone post-login landing for the IPO platform.
 *
 * ISOLATED, SEPARATE BUILD. Imports nothing from the main Dashboard / IPOLabs app.
 * Faithful 1:1 React port of `ipo_platform_landing_dashboard.html`. The nav's
 * "Global Reseller Program" button opens the second screen, a 1:1 port of
 * `global_reseller_program_page.html`. Styles in scoped `DashboardNew.css`.
 * Tabler icons + Playfair/DM Sans loaded in public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import AppFooter from "@/components/AppFooter";
import BecomePartnerFlow from "./BecomePartnerFlow";
import JoinInvestorFlow from "./JoinInvestorFlow";
import "./DashboardNew.css";

const DashboardNew = ({ user, apiClient, hideSidebar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [screen, setScreen] = useState(
    new URLSearchParams(location.search).get("screen") === "reseller" ? "reseller" : "landing"
  ); // "landing" | "reseller" | "become-partner" | "join-investor"
  const [profileOpen, setProfileOpen] = useState(false);

  // Sync the active screen with the URL ?screen= param. Navigating to
  // /dashboard-new?screen=reseller (e.g. from the sidebar "Global Reseller
  // Program" button) opens the reseller page; navigating to /dashboard-new
  // returns to the landing view. In-page sub-screens (become-partner /
  // join-investor) don't change the URL, so this won't fight them.
  useEffect(() => {
    const s = new URLSearchParams(location.search).get("screen");
    setScreen(s === "reseller" ? "reseller" : "landing");
  }, [location.search]);

  const sendPrompt = () => {}; // standalone build — AI prompt hooks inert here

  // Logout (used by the header profile menu on the sidebar-less New User Landing
  // Page, which otherwise has no logout affordance from the removed sidebar).
  const handleLogout = async () => {
    try { await apiClient?.post("/auth/logout"); } catch { /* best-effort */ }
    try { localStorage.removeItem("session_token"); } catch { /* ignore */ }
    try { sessionStorage.removeItem("ipolabs_consent_acked"); } catch { /* ignore */ }
    navigate("/login");
  };

  const userName = user?.name || user?.email || "";
  const userEmail = user?.email || "";
  const userInitials =
    (userName || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "RK";

  return (
    <div className="flex min-h-screen bg-white">
      {!hideSidebar && <Sidebar user={user} apiClient={apiClient} />}
      <main className={`flex-1 ${hideSidebar ? "" : "ml-64"}`}>
        <div className="dash-new-root" data-testid={hideSidebar ? "new-user-landing-page" : "dashboard-new-page"}>
          {/* Header = IPONavigator top nav (moved up from the hero box) */}
          <div className="lp-nav dn-header">
            <div className="lp-brand" onClick={() => navigate(hideSidebar ? "/home" : "/dashboard-new")} style={{ cursor: "pointer" }} data-testid="dn-brand">
              IPO<span>Navigator</span>
            </div>
            <div className="lp-links">
              <div className={`lp-link ${screen === "landing" ? "on" : ""}`} onClick={() => setScreen("landing")} data-testid="dn-nav-home">Home</div>
              <div className="lp-link">Journey</div>
              <div className="lp-link">Advisors</div>
              <div className="lp-link">Funding</div>
            </div>
            <div className="reseller-link" onClick={() => setScreen("reseller")} data-testid="reseller-link">
              <i className="ti ti-world-share" aria-hidden="true" /> Global Reseller Program
            </div>
            {hideSidebar ? (
              <div className="dn-profile" style={{ position: "relative", marginLeft: 12 }}>
                <div
                  className="lp-av"
                  style={{ cursor: "pointer" }}
                  onClick={() => setProfileOpen((v) => !v)}
                  data-testid="dn-profile-avatar"
                  title={userName}
                >
                  {userInitials}
                </div>
                {profileOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setProfileOpen(false)} />
                    <div className="dn-profile-menu" data-testid="dn-profile-menu" style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: "#fff", border: "1px solid #e8e6f0", borderRadius: 12, boxShadow: "0 14px 34px rgba(20,20,30,.14)", minWidth: 220, zIndex: 50, overflow: "hidden" }}>
                      <div style={{ padding: "12px 14px", borderBottom: "1px solid #f0eff5" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#16161e" }}>{userName}</div>
                        <div style={{ fontSize: 11.5, color: "#8a8a94" }}>{userEmail}</div>
                      </div>
                      <button
                        onClick={handleLogout}
                        data-testid="dn-logout-btn"
                        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "11px 14px", border: "none", background: "none", color: "#c0392b", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
                      >
                        <i className="ti ti-logout" aria-hidden="true" /> Log Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="lp-av" style={{ marginLeft: 12 }}>{userInitials}</div>
            )}
          </div>

          <div className="dn-shell">
            {screen === "landing" && <Landing sendPrompt={sendPrompt} onEnterWorkspace={() => navigate("/ipo-workspace-onboarding")} onRegisterAdvisor={() => navigate("/advisor-onboarding")} onJoinInvestor={() => setScreen("join-investor")} />}
            {screen === "reseller" && <Reseller sendPrompt={sendPrompt} onBecomePartner={() => setScreen("become-partner")} />}
            {screen === "become-partner" && <BecomePartnerFlow onBack={() => setScreen("reseller")} />}
            {screen === "join-investor" && <JoinInvestorFlow onBack={() => setScreen("landing")} />}
          </div>
        </div>

        {/* ── Site footer (Oracle {IPO.GPT} navy-blue style) ── */}
        <AppFooter variant="navy" />
      </main>
    </div>
  );
};

/* ─── Oracle knowledge-hub banner (integrates with /oracle) ─── */
const ORACLE_CHIPS = [
  { icon: "ti-scale", label: "Main Board vs SME?", q: "Main Board vs SME — which IPO route is right for my company?" },
  { icon: "ti-route", label: "The IPO process", q: "Walk me through the full IPO process in India, step by step." },
  { icon: "ti-calculator", label: "How am I valued?", q: "How is my company valued for an IPO in India?" },
  { icon: "ti-clock-dollar", label: "Cost & timeline", q: "What does an IPO cost and how long does it take in India?" },
];

const OracleBanner = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const go = (text) => {
    const query = (typeof text === "string" ? text : q).trim();
    if (!query) return;
    navigate(`/oracle?q=${encodeURIComponent(query)}`);
  };
  return (
    <div className="ob" data-testid="oracle-banner">
      <div className="ob-glow" aria-hidden="true" />
      <div className="ob-inner">
        <div className="ob-top">
          <div className="ob-logo"><i className="ti ti-sparkles" aria-hidden="true" /></div>
          <div className="ob-brand">Oracle <span>{"{IPO.GPT}"}</span></div>
          <span className="ob-tag">Knowledge Hub</span>
        </div>
        <div className="ob-h">Understand the entire IPO journey — in plain language.</div>
        <div className="ob-sub">Ask anything about going public in India — eligibility, costs, timelines, DRHP, SEBI rules. Oracle answers with cited, India-specific sources.</div>
        <div className="ob-bar">
          <i className="ti ti-search ob-bar-ic" aria-hidden="true" />
          <input
            className="ob-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            placeholder="Ask Oracle anything about IPOs in India…"
            data-testid="oracle-banner-input"
          />
          <button className="ob-ask" onClick={() => go()} data-testid="oracle-banner-ask">Ask →</button>
        </div>
        <div className="ob-chips">
          {ORACLE_CHIPS.map((c, i) => (
            <button key={i} className="ob-chip" onClick={() => go(c.q)} data-testid={`oracle-chip-${i}`}>
              <i className={`ti ${c.icon}`} aria-hidden="true" /> {c.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Landing screen ─── */
const Landing = ({ sendPrompt, onEnterWorkspace, onRegisterAdvisor, onJoinInvestor }) => (
  <div className="lp">
    <h2 className="sr-only">
      Post-login landing for the end-to-end IPO management platform. Three entry paths: corporate
      clients (MSME founders and CXOs), subject-matter experts joining the advisor marketplace, and
      capital providers joining the IPO funding platform. A separate Global Reseller Program link
      sits in the top navigation.
    </h2>

    <div className="hero">
      <div className="hero-kick">End-to-end IPO management platform</div>
      <div className="hero-h">From first thought to listing bell — your entire IPO journey, in one place.</div>
      <div className="hero-sub">Built for founders, MSMEs, promoters and CFOs ready to go public. Assemble your advisor team, draft your DRHP, raise pre-IPO and bridge capital, and reach the exchange — guided at every step.</div>
      <div className="hero-stats">
        <div><div className="hs-v">6-stage</div><div className="hs-l">guided journey</div></div>
        <div><div className="hs-v">214+</div><div className="hs-l">verified advisors</div></div>
        <div><div className="hs-v">₹2.4L Cr</div><div className="hs-l">capital network</div></div>
      </div>
    </div>

    <div className="choose">Choose how you want to begin</div>

    <div className="cards">
      <div className="uc primary">
        <span className="uc-flag">Start here</span>
        <div className="uc-ic" style={{ background: "#E6F1FB", color: "#0C447C" }}><i className="ti ti-building-skyscraper" aria-hidden="true" /></div>
        <div className="uc-h">I want to take my company public</div>
        <div className="uc-who">MSME founders · promoters · CEOs · CFOs</div>
        <div className="uc-d">Run your full IPO — readiness score, advisor team, DRHP drafting, funding and listing — with a guided roadmap built for first-timers.</div>
        <div className="uc-feat">
          <div className="uc-f"><i className="ti ti-route" style={{ color: "#185FA5" }} aria-hidden="true" /> 6-stage IPO journey tracker</div>
          <div className="uc-f"><i className="ti ti-file-text" style={{ color: "#185FA5" }} aria-hidden="true" /> Collaborative DRHP workspace</div>
          <div className="uc-f"><i className="ti ti-cash" style={{ color: "#185FA5" }} aria-hidden="true" /> Pre-IPO &amp; bridge funding desk</div>
        </div>
        <button className="uc-btn solid" onClick={onEnterWorkspace} data-testid="enter-workspace-btn">Enter my IPO workspace →</button>
      </div>

      <div className="uc">
        <div className="uc-ic" style={{ background: "#E1F5EE", color: "#085041" }}><i className="ti ti-briefcase" aria-hidden="true" /></div>
        <div className="uc-h">I&apos;m an IPO professional</div>
        <div className="uc-who">CA · CS · CFO · legal · auditors · RTA</div>
        <div className="uc-d">Join the Advisor Marketplace, list your verified credentials and get matched to MSMEs assembling their IPO teams.</div>
        <div className="uc-feat">
          <div className="uc-f"><i className="ti ti-rosette-discount-check" style={{ color: "#0F6E56" }} aria-hidden="true" /> SEBI-verified profile</div>
          <div className="uc-f"><i className="ti ti-users" style={{ color: "#0F6E56" }} aria-hidden="true" /> Inbound mandate matching</div>
          <div className="uc-f"><i className="ti ti-star" style={{ color: "#0F6E56" }} aria-hidden="true" /> Track record &amp; ratings</div>
        </div>
        <button className="uc-btn out" onClick={onRegisterAdvisor} data-testid="register-advisor-btn">Register as an advisor →</button>
      </div>

      <div className="uc">
        <div className="uc-ic" style={{ background: "#EEEDFE", color: "#3C3489" }}><i className="ti ti-coins" aria-hidden="true" /></div>
        <div className="uc-h">I want to fund IPOs</div>
        <div className="uc-who">banks · HNIs · VCs · AIFs · family offices</div>
        <div className="uc-d">Join the IPO Funding Platform to discover deal flow across pre-IPO, anchor, bridge and post-IPO rounds from vetted MSMEs.</div>
        <div className="uc-feat">
          <div className="uc-f"><i className="ti ti-target-arrow" style={{ color: "#3C3489" }} aria-hidden="true" /> Curated MSME deal flow</div>
          <div className="uc-f"><i className="ti ti-shield-check" style={{ color: "#3C3489" }} aria-hidden="true" /> KYC-verified issuers</div>
          <div className="uc-f"><i className="ti ti-chart-arrows" style={{ color: "#3C3489" }} aria-hidden="true" /> Pre, bridge &amp; post rounds</div>
        </div>
        <button className="uc-btn out" onClick={onJoinInvestor} data-testid="join-investor-btn">Join as an investor →</button>
      </div>
    </div>

    <OracleBanner />
  </div>
);

/* ─── Global Reseller Program screen ─── */
const Reseller = ({ sendPrompt, onBecomePartner }) => (
  <div className="rp">
    <h2 className="sr-only">
      Global Reseller Program page — a Pan-India referral program paying 25% revenue share per deal,
      with partner tiers for large e-commerce resellers, CA firms, independent CFOs and banks, focused
      on SME and MSME IPO mandates.
    </h2>

    <div className="rp-hero">
      <div className="rp-kick"><i className="ti ti-world-share" aria-hidden="true" /> Global Reseller Program</div>
      <div className="rp-h">Refer an SME to IPO. Earn 25% on every deal — Pan-India.</div>
      <div className="rp-sub">A partner network for those already trusted by India&apos;s businesses — large resellers, CA firms, fractional CFOs and banks. Bring MSMEs into their IPO journey and share in the revenue of every mandate you originate.</div>
      <div className="rp-share">
        <div className="rp-share-v">25%</div>
        <div className="rp-share-t">revenue share on every deal you bring in, across advisory, funding and platform fees — recurring through the mandate lifecycle.</div>
      </div>
    </div>

    <div className="sec-lbl">Who can become a partner</div>
    <div className="tiers">
      <div className="tier">
        <div className="tier-top">
          <div className="tier-ic" style={{ background: "#FAEEDA", color: "#854F0B" }}><i className="ti ti-building-store" aria-hidden="true" /></div>
          <div><div className="tier-n">E-commerce platforms</div><div className="tier-w">Large resellers · ₹5 Cr+ profit</div></div>
          <span className="tier-tag" style={{ background: "#FAEEDA", color: "#854F0B" }}>Enterprise</span>
        </div>
        <div className="tier-d">Marketplaces and aggregators with deep merchant networks — surface IPO-readiness to your top-performing sellers as a value-add.</div>
      </div>
      <div className="tier">
        <div className="tier-top">
          <div className="tier-ic" style={{ background: "#E1F5EE", color: "#085041" }}><i className="ti ti-calculator" aria-hidden="true" /></div>
          <div><div className="tier-n">CA &amp; CS firms</div><div className="tier-w">Practices with SME client books</div></div>
          <span className="tier-tag" style={{ background: "#E1F5EE", color: "#085041" }}>Channel</span>
        </div>
        <div className="tier-d">Your clients already trust you with their books. Refer the IPO-ready ones and earn on advisory, DRHP and funding revenue.</div>
      </div>
      <div className="tier">
        <div className="tier-top">
          <div className="tier-ic" style={{ background: "#E6F1FB", color: "#0C447C" }}><i className="ti ti-user-dollar" aria-hidden="true" /></div>
          <div><div className="tier-n">Independent / fractional CFOs</div><div className="tier-w">Part-time finance leaders</div></div>
          <span className="tier-tag" style={{ background: "#E6F1FB", color: "#0C447C" }}>Individual</span>
        </div>
        <div className="tier-d">Sitting across multiple MSME boards, you see who&apos;s ready before anyone else. Originate mandates and earn on each.</div>
      </div>
      <div className="tier">
        <div className="tier-top">
          <div className="tier-ic" style={{ background: "#EEEDFE", color: "#3C3489" }}><i className="ti ti-building-bank" aria-hidden="true" /></div>
          <div><div className="tier-n">Banks &amp; NBFCs</div><div className="tier-w">Relationship-led institutions</div></div>
          <span className="tier-tag" style={{ background: "#EEEDFE", color: "#3C3489" }}>Institutional</span>
        </div>
        <div className="tier-d">Channel your MSME lending relationships into IPO mandates and co-earn across the funding and advisory stack.</div>
      </div>
    </div>

    <div className="sec-lbl">How the referral flow works</div>
    <div className="flow">
      <div className="fstep"><div className="fnum">1</div><div className="fh">Refer</div><div className="fd">Submit an MSME via your partner dashboard or unique link</div></div>
      <div className="fstep"><div className="fnum">2</div><div className="fh">Onboard</div><div className="fd">We run readiness &amp; KYC; you stay looped in</div></div>
      <div className="fstep"><div className="fnum">3</div><div className="fh">Engage</div><div className="fd">MSME activates advisory, DRHP or funding</div></div>
      <div className="fstep"><div className="fnum">4</div><div className="fh">Earn</div><div className="fd">25% share paid as revenue is recognised</div></div>
    </div>

    <div className="sec-lbl">Illustrative partner economics</div>
    <div className="econ">
      <div className="ec"><div className="ec-v">₹3–8 L</div><div className="ec-l">Avg platform revenue / SME mandate</div></div>
      <div className="ec"><div className="ec-v">₹75K–2 L</div><div className="ec-l">Your 25% share per deal</div></div>
      <div className="ec"><div className="ec-v">Recurring</div><div className="ec-l">Across mandate lifecycle, not one-off</div></div>
    </div>

    <div className="rp-cta">
      <span className="rp-msg">Partners get a dashboard with referral tracking, attribution, payout statements and co-branded collateral. Focused on SME &amp; MSME IPO mandates Pan-India.</span>
      <button className="rp-btn out" onClick={sendPrompt} data-testid="reseller-terms">See full terms ↗</button>
      <button className="rp-btn" onClick={onBecomePartner} data-testid="reseller-become-partner">Become a partner ↗</button>
    </div>
  </div>
);

export default DashboardNew;
